import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Reveal } from "../components/Reveal";
import { COLLAB_IMG, HERO_IMAGES, CATEGORY_IMAGES } from "../mock";
import CORPORATE_TRAINING_HERO_IMG from "../assets/corporate-training-hero.png";
import {
  Star, Layers, Clock, Users, Brain, Cloud, ShieldCheck, Settings2,
  Video, Building2, Wrench, Layers3,
  ArrowRight, CheckCircle2
} from "lucide-react";

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#15348C] bg-[#15348C]/[0.06] border border-[#15348C]/10 px-4 py-1.5 rounded-full">
    <span className="w-1.5 h-1.5 rounded-full bg-[#F57C20]"/>
    {children}
  </span>
);

const OUTCOMES = [
  { icon: Star, value: "98%", label: "Client Satisfaction", desc: "Customized learning experiences designed around enterprise requirements." },
  { icon: Layers, value: "500+", label: "Enterprise Batches Delivered", desc: "Training programs delivered across technologies, teams, and business functions." },
  { icon: Clock, value: "100,000+", label: "Learning Hours Delivered", desc: "Practical learning experiences led by experienced industry trainers." },
  { icon: Users, value: "100+", label: "Enterprise Trainers", desc: "Access to subject matter experts across emerging technology domains." }
];

const TECH_AREAS = [
  { icon: Brain, image: CATEGORY_IMAGES.ai, title: "AI & Generative AI", desc: "Practical programs covering Artificial Intelligence, Generative AI, Machine Learning, Prompt Engineering, AI Governance, and enterprise use cases." },
  { icon: Cloud, image: CATEGORY_IMAGES.cloud, title: "Cloud & Data", desc: "Role-based training across cloud platforms, data engineering, analytics, and modern technology environments." },
  { icon: ShieldCheck, image: CATEGORY_IMAGES.security, title: "Cybersecurity", desc: "Training programs covering cybersecurity concepts, security practices, risk, compliance, and enterprise requirements." },
  { icon: Settings2, image: CATEGORY_IMAGES.business, title: "Customized Technology Programs", desc: "Training designed around your technology stack, team roles, business priorities, and specific learning requirements." }
];

const DELIVERY_FORMATS = [
  { icon: Video, title: "Live Virtual Training", desc: "Interactive instructor-led sessions delivered online for teams across different locations." },
  { icon: Building2, title: "Classroom Training", desc: "Focused, instructor-led programs conducted at your location or a preferred training venue." },
  { icon: Wrench, title: "Hands-On Workshops", desc: "Practical workshops with labs, exercises, and real-world scenarios." },
  { icon: Layers3, title: "Blended Learning Programs", desc: "A combination of instructor-led sessions, practical labs, self-paced learning, and workshops." }
];

const CorporateTrainingPage = () => {
  return (
    <div className="bg-white min-h-screen">
      <Header/>

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#0A1F5C] via-[#15348C] to-[#1E47A8] text-white overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{ backgroundImage: "radial-gradient(#fff 1px, transparent 1px)", backgroundSize: "26px 26px" }}
        />
        <div className="absolute -top-24 -right-16 w-80 h-80 bg-[#6BBE32]/20 rounded-full blur-[100px]"/>
        <div className="absolute -bottom-24 -left-16 w-80 h-80 bg-[#F57C20]/20 rounded-full blur-[100px]"/>
        <div className="relative max-w-7xl mx-auto px-4 py-14 lg:py-16 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <span className="inline-flex items-center gap-2 text-xs font-bold tracking-widest text-[#8BD835] bg-white/5 border border-white/10 px-4 py-2 rounded-full mb-7">
              <span className="w-1.5 h-1.5 rounded-full bg-[#8BD835] sg-pulse"/>
              ENTERPRISE TRAINING PROGRAMS
            </span>
            <h1 className="text-4xl lg:text-5xl font-bold leading-[1.12]">Enterprise Training That Aligns with Your Technology Priorities</h1>
            <p className="mt-5 text-gray-300 text-base lg:text-lg leading-relaxed max-w-xl">
              Customized instructor-led programs that help organizations strengthen capabilities across AI, Generative AI, cloud, data, cybersecurity, and emerging technologies.
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                Talk to Our Expert <ArrowRight size={16}/>
              </Link>
            </div>
          </Reveal>
          <Reveal variant="right" delay={100}>
            <div className="relative">
              <img
                src={CORPORATE_TRAINING_HERO_IMG}
                alt="Enterprise corporate training session"
                className="w-full h-[340px] lg:h-[420px] object-cover rounded-2xl shadow-2xl ring-1 ring-white/10"
              />
              <div className="absolute inset-0 rounded-2xl bg-gradient-to-t from-[#0A1F5C]/40 via-transparent to-transparent"/>
              <div className="absolute -bottom-7 -left-6 sm:left-0 bg-white rounded-xl shadow-xl p-4 pr-6 flex items-center gap-3.5 max-w-[240px]">
                <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                  <Star size={18} className="text-[#6BBE32]"/>
                </div>
                <div>
                  <div className="text-lg font-bold text-[#15348C] leading-none">98%</div>
                  <div className="text-[11px] text-gray-500 mt-1">Client Satisfaction</div>
                </div>
              </div>
              <div className="absolute -top-5 -right-4 bg-white rounded-xl shadow-xl px-4 py-3 flex items-center gap-2.5">
                <Users size={16} className="text-[#F57C20]"/>
                <div className="text-xs font-bold text-[#15348C]">100+ Enterprise Trainers</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Technology areas */}
      <section className="bg-[#F8F9FA] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>WHERE SKILLSGAIN DELIVERS IMPACT</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Enterprise Training Across Critical Technology Areas</h2>
              <p className="mt-3 text-gray-600">Programs designed around the technologies and capabilities organizations need today.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {TECH_AREAS.map((t, i) => (
              <Reveal key={t.title} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 rounded-2xl overflow-hidden h-full shadow-sm">
                  <div className="relative h-36 overflow-hidden">
                    <img src={t.image} alt={t.title} className="sg-img-zoom w-full h-full object-cover"/>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F5C]/70 via-[#0A1F5C]/10 to-transparent"/>
                    <div className="absolute bottom-3 left-3 w-11 h-11 rounded-xl bg-[#15348C] group-hover:bg-[#6BBE32] transition-colors flex items-center justify-center shadow-lg">
                      <t.icon size={20} className="text-white"/>
                    </div>
                  </div>
                  <div className="p-6">
                    <h4 className="font-bold text-[#15348C] text-lg">{t.title}</h4>
                    <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{t.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Flexible delivery */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img src={COLLAB_IMG} alt="Enterprise training delivery" className="rounded-2xl shadow-xl ring-1 ring-black/5 w-full h-[440px] object-cover"/>
          </Reveal>
          <Reveal variant="right" delay={80}>
            <Eyebrow>FLEXIBLE DELIVERY</Eyebrow>
            <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Choose the Training Format That Works for Your Teams</h2>
            <p className="mt-3 text-gray-600 leading-relaxed">Deliver learning experiences across locations and teams through formats suited to your organizational requirements.</p>
            <div className="mt-8 grid sm:grid-cols-2 gap-4">
              {DELIVERY_FORMATS.map(d => (
                <div key={d.title} className="sg-card flex gap-3.5 bg-white border border-gray-200/80 rounded-xl p-5 shadow-sm">
                  <div className="w-10 h-10 shrink-0 rounded-lg bg-[#6BBE32]/12 flex items-center justify-center">
                    <d.icon size={18} className="text-[#6BBE32]"/>
                  </div>
                  <div>
                    <div className="font-bold text-[#15348C] text-sm">{d.title}</div>
                    <p className="text-xs text-gray-500 mt-1.5 leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Measurable outcomes — by the numbers */}
      <section className="bg-[#FFFAF0] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-10">
              <Eyebrow>MEASURABLE OUTCOMES</Eyebrow>
              <h2 className="mt-4 text-3xl lg:text-4xl font-bold text-[#15348C]">Training That Delivers More Than Course Completion</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {OUTCOMES.map((o, i) => (
              <Reveal key={o.label} variant="up" delay={i * 80}>
                <div className="sg-card group bg-white border border-gray-200/80 border-t-4 border-t-transparent hover:border-t-[#6BBE32] rounded-2xl p-7 h-full shadow-sm">
                  <div className="w-12 h-12 rounded-xl bg-[#6BBE32]/12 flex items-center justify-center mb-5 group-hover:bg-[#6BBE32] transition-colors">
                    <o.icon size={22} className="text-[#6BBE32] group-hover:text-white transition-colors"/>
                  </div>
                  <div className="text-4xl font-bold text-[#15348C] tracking-tight">{o.value}</div>
                  <div className="text-sm font-semibold text-[#15348C] mt-2">{o.label}</div>
                  <p className="text-sm text-gray-500 mt-2.5 leading-relaxed">{o.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Business alignment statement */}
      <section className="bg-[#F8F9FA] py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal variant="left">
            <img
              src={HERO_IMAGES[1]}
              alt="Enterprise learning aligned to business requirements"
              className="w-full h-[380px] object-cover rounded-2xl shadow-xl ring-1 ring-black/5"
            />
          </Reveal>
          <Reveal variant="right" delay={80}>
            <div className="w-12 h-1 bg-[#F57C20] rounded-full mb-8"/>
            <h2 className="text-2xl lg:text-3xl font-bold text-[#15348C] leading-snug">Enterprise Learning That Supports Real Business Requirements</h2>
            <p className="mt-5 text-gray-600 leading-relaxed">
              Skillsgain works with organizations to plan and deliver training programs aligned with their technology priorities, team requirements, and business objectives.
            </p>
            <p className="mt-4 text-gray-600 leading-relaxed">
              From AI and Generative AI to cloud, data, cybersecurity, and other emerging technologies, our programs combine experienced trainers, practical learning, and customized delivery models.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-white py-12 lg:py-14">
        <div className="max-w-7xl mx-auto px-4">
          <Reveal variant="scale">
            <div className="bg-gradient-to-br from-[#15348C] to-[#0A1F5C] rounded-3xl p-10 lg:p-16 relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-64 h-64 bg-[#6BBE32]/20 rounded-full blur-3xl"/>
              <div className="absolute -bottom-20 -left-20 w-64 h-64 bg-[#F57C20]/20 rounded-full blur-3xl"/>
              <div className="relative grid lg:grid-cols-2 gap-10 items-center">
                <div>
                  <div className="text-xs font-bold tracking-widest text-[#8BD835] mb-3">FOR LEARNING &amp; BUSINESS LEADERS</div>
                  <h2 className="text-3xl lg:text-4xl font-bold text-white leading-tight">Plan Your Enterprise Training Program</h2>
                  <p className="mt-4 text-gray-300 leading-relaxed">Tell us about your technology priorities, team requirements, and learning objectives. Our experts will help you identify the right training approach for your organization.</p>
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2.5 text-xs text-gray-300">
                    {["Customized training approach", "Expert-led programs", "Practical learning experiences"].map(t => (
                      <span key={t} className="flex items-center gap-1.5"><CheckCircle2 size={14} className="text-[#8BD835]"/>{t}</span>
                    ))}
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 lg:justify-end">
                  <Link to="/contact" className="sg-btn-glow inline-flex items-center gap-2 bg-[#F57C20] hover:bg-[#D86810] text-white font-semibold px-6 py-3.5 rounded-lg transition-colors">
                    Talk to Our Advisor <ArrowRight size={16}/>
                  </Link>
                  <Link to="/contact" className="border-2 border-white/30 text-white font-semibold px-6 py-3.5 rounded-lg hover:bg-white/10 hover:border-[#8BD835] transition-colors">Request a Corporate Proposal</Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer/>
    </div>
  );
};

export default CorporateTrainingPage;
